import React from 'react'
import { Box, Typography, Link, Container, Grid } from '@mui/material'
import { styled } from '@mui/material/styles'
import { SVGS } from '@/assets/svgs'
import SVG from 'react-inlinesvg'
import { Home, GridView, Help, Info } from '@mui/icons-material'
import FullLogo from '@/assets/svgs/full-logo.svg'

const FooterContainer = styled(Box)(({ theme }) => ({
  background: `
    linear-gradient(135deg, rgba(99, 102, 241, 0.02) 0%, rgba(168, 85, 247, 0.015) 100%),
    radial-gradient(circle at top left, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
    radial-gradient(circle at bottom right, rgba(168, 85, 247, 0.04) 0%, transparent 50%),
    #ffffff
  `,
  borderTop: '2px solid rgba(99, 102, 241, 0.08)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.2), transparent)',
  },
}))

const FooterContent = styled(Container)(({ theme }) => ({
  paddingTop: '80px',
  paddingBottom: '48px',
  position: 'relative',
  zIndex: 1,

  [theme.breakpoints.down('md')]: {
    paddingTop: '60px',
    paddingBottom: '40px',
  },
}))

const FooterColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

const ColumnTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#0f172a',
  marginBottom: '8px',
  letterSpacing: '-0.01em',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',

  '& svg': {
    fontSize: '20px',
    color: '#6366f1',
  },
})

const FooterLink = styled(Link)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#64748b',
  textDecoration: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  width: 'fit-content',
  position: 'relative',

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -2,
    left: 0,
    width: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
    transition: 'width 0.3s ease',
  },

  '&:hover': {
    color: '#6366f1',
    transform: 'translateX(4px)',

    '&::after': {
      width: '100%',
    },
  },
})

const CopyrightContainer = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(226, 232, 240, 0.6)',
  paddingTop: '32px',
  paddingBottom: '32px',
  marginTop: '60px',

  [theme.breakpoints.down('md')]: {
    marginTop: '40px',
    paddingTop: '24px',
    paddingBottom: '24px',
  },
}))

const SocialButton = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  borderRadius: '12px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '2px solid rgba(99, 102, 241, 0.12)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  textDecoration: 'none',

  '& svg': {
    fontSize: '20px',
  },

  '&:hover': {
    backgroundColor: '#6366f1',
    borderColor: '#6366f1',
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',

    '& svg': {
      filter: 'brightness(0) invert(1)',
    },
  },
})

const BrandSection = styled(Box)({
  marginBottom: '32px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
})

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'opacity 0.2s',

  '&:hover': {
    opacity: 0.8,
  },
})

const BrandTagline = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#64748b',
  lineHeight: 1.6,
  maxWidth: '280px',
})

function Footer() {
  return (
    <FooterContainer>
      <FooterContent maxWidth='lg'>
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            <BrandSection>
              <LogoContainer onClick={() => (window.location.href = '/')}>
                <SVG src={FullLogo} width={140} height={42} style={{ display: 'block' }} />
              </LogoContainer>
              <BrandTagline>
                Create stunning patterns and designs with our powerful editor. Join thousands of
                creators worldwide.
              </BrandTagline>
            </BrandSection>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} md={2}>
            <FooterColumn>
              <ColumnTitle>
                <Home />
                Quick Links
              </ColumnTitle>
              <FooterLink href='/'>Home</FooterLink>
              <FooterLink href='#pricing'>Pricing Plans</FooterLink>
              <FooterLink href='#terms'>Terms & Privacy</FooterLink>
            </FooterColumn>
          </Grid>

          {/* Explore */}
          <Grid item xs={6} md={2}>
            <FooterColumn>
              <ColumnTitle>
                <GridView />
                Explore
              </ColumnTitle>
              <FooterLink href='/discover/pattern'>Patterns</FooterLink>
              <FooterLink href='/discover/album'>Collections</FooterLink>
              <FooterLink href='/discover/gallery'>Galleries</FooterLink>
            </FooterColumn>
          </Grid>

          {/* Community */}
          <Grid item xs={6} md={2}>
            <FooterColumn>
              <ColumnTitle>
                <Help />
                Community
              </ColumnTitle>
              <FooterLink href='#support'>Support</FooterLink>
              <FooterLink href='#help'>Help Center</FooterLink>
              <FooterLink href='#faq'>FAQ</FooterLink>
            </FooterColumn>
          </Grid>

          {/* Company */}
          <Grid item xs={6} md={2}>
            <FooterColumn>
              <ColumnTitle>
                <Info />
                Company
              </ColumnTitle>
              <FooterLink href='#about'>About Us</FooterLink>
              <FooterLink href='#contact'>Contact</FooterLink>
            </FooterColumn>
          </Grid>
        </Grid>

        {/* Copyright & Social */}
        <CopyrightContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#64748b',
              }}
            >
              © {new Date().getFullYear()} MeshDom. All Rights Reserved.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <SocialButton href='#' aria-label='Facebook'>
                <SVG src={SVGS.SocialFacebook} />
              </SocialButton>
              <SocialButton href='#' aria-label='Twitter'>
                <SVG src={SVGS.SocialTwitter} />
              </SocialButton>
              <SocialButton href='#' aria-label='YouTube'>
                <SVG src={SVGS.SocialYoutube} />
              </SocialButton>
              <SocialButton href='#' aria-label='Instagram'>
                <SVG src={SVGS.SocialInstagram} />
              </SocialButton>
            </Box>
          </Box>
        </CopyrightContainer>
      </FooterContent>
    </FooterContainer>
  )
}

export default Footer
